import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasAccess } from "@/lib/roles";
import { Plus, DollarSign, CheckCircle2, AlertTriangle, Search, FileDown } from "lucide-react";

function money(v) {
  const n = Number(v || 0);
  return `$${n.toFixed(2)}`;
}
function humanTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

const STATUS_META = {
  balanced:  { cls: "bg-emerald-500/10 text-emerald-300 border-emerald-400/40", icon: CheckCircle2, label: "Balanced" },
  mismatch:  { cls: "bg-[color:var(--warning)]/10 text-[color:var(--warning)] border-[color:var(--warning)]/40", icon: AlertTriangle, label: "Mismatch" },
  corrected: { cls: "bg-purple-500/10 text-purple-300 border-purple-400/40", icon: CheckCircle2, label: "Correction" },
  reviewed:  { cls: "bg-blue-500/10 text-blue-300 border-blue-400/40", icon: CheckCircle2, label: "Reviewed" },
};

export default function FloatLog() {
  const { user } = useAuth();
  const canReview = hasAccess(user?.role, ["admin", "gm"]);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [managerId, setManagerId] = useState("");
  const [registerId, setRegisterId] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [managers, setManagers] = useState([]);
  const [registers, setRegisters] = useState([]);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status !== "all") params.set("status_", status);
    if (managerId) params.set("manager_id", managerId);
    if (registerId) params.set("register_id", registerId);
    if (start) params.set("start", start);
    if (end) params.set("end", end);
    api.get(`/float_changes?${params.toString()}`).then(({ data }) => setItems(data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/float_dashboard").then(({ data }) => setStats(data)).catch(() => {});
  }, [q, status, managerId, registerId, start, end]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (canReview) api.get("/employees").then(({ data }) => setManagers(data.filter((e) => ["manager", "assistant_manager", "inventory_manager", "admin", "gm"].includes(e.role)))).catch(() => {});
    api.get("/registers").then(({ data }) => setRegisters(data)).catch(() => {});
  }, [canReview]);

  const mismatches = useMemo(() => items.filter((i) => i.status === "mismatch"), [items]);

  return (
    <div className="space-y-6" data-testid="float-log-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-caps">Cash Handling</div>
          <h1 className="font-heading text-3xl sm:text-4xl">Float Change Log Book</h1>
        </div>
        <Link to="/float/new" className="hos-btn-primary flex items-center gap-2" data-testid="record-float-btn">
          <Plus size={16}/> Record Float Change
        </Link>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card k="Today's Changes" v={stats.today} icon={DollarSign}/>
          <Card k="Balanced" v={stats.balanced} accent="emerald"/>
          <Card k="Mismatches" v={stats.mismatches} accent={stats.mismatches ? "warning" : undefined}/>
          <Card k="Managers" v={stats.managers}/>
        </div>
      )}

      {canReview && mismatches.length > 0 && (
        <div className="hos-card p-4 border border-[color:var(--warning)]/50 bg-[color:var(--warning)]/5">
          <div className="flex items-center gap-2 mb-2 text-[color:var(--warning)] font-semibold">
            <AlertTriangle size={16}/> Needs Review — {mismatches.length}
          </div>
          <div className="space-y-1">
            {mismatches.slice(0, 5).map((m) => (
              <Link to={`/float/${m.id}`} key={m.id} className="flex flex-wrap items-center justify-between text-sm p-2 rounded bg-[color:var(--bg-surface-2)] hover:bg-[color:var(--bg-surface-3)]" data-testid={`mismatch-${m.id}`}>
                <div><span className="font-semibold text-[color:var(--gold)]">{m.reference_number}</span> · {m.register_name} · {m.manager_name}</div>
                <div>Diff <span className="text-[color:var(--warning)] font-bold">{money(m.difference)}</span> · {humanTime(m.created_at)}</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="hos-card p-4 grid grid-cols-2 md:grid-cols-6 gap-2">
        <div className="col-span-2 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-secondary)]"/>
          <input className="hos-input pl-8" placeholder="Search reference or notes…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="float-search"/>
        </div>
        <select className="hos-input" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="float-status">
          <option value="all">All statuses</option>
          <option value="balanced">Balanced</option>
          <option value="mismatch">Mismatch</option>
          <option value="corrected">Correction</option>
          <option value="reviewed">Reviewed</option>
        </select>
        <select className="hos-input" value={registerId} onChange={(e) => setRegisterId(e.target.value)} data-testid="float-register">
          <option value="">All registers</option>
          {registers.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        {canReview && (
          <select className="hos-input" value={managerId} onChange={(e) => setManagerId(e.target.value)}>
            <option value="">All managers</option>
            {managers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        )}
        <input type="date" className="hos-input" value={start} onChange={(e) => setStart(e.target.value)}/>
        <input type="date" className="hos-input" value={end} onChange={(e) => setEnd(e.target.value)}/>
      </div>

      <div className="hos-card overflow-hidden">
        <table className="w-full text-sm hidden md:table">
          <thead className="text-left label-caps">
            <tr><th className="p-3">Date/Time</th><th>Register</th><th>Taken</th><th>Replaced</th><th>Diff</th><th>Manager</th><th>Ref</th></tr>
          </thead>
          <tbody>
            {items.map((i) => {
              const M = STATUS_META[i.status] || STATUS_META.balanced;
              return (
                <tr key={i.id} className="border-t border-[color:var(--border)] hover:bg-[color:var(--bg-surface-2)]" data-testid={`float-row-${i.id}`}>
                  <td className="p-3">{humanTime(i.created_at)}</td>
                  <td>{i.register_name}</td>
                  <td>{money(i.taken_total)}</td>
                  <td>{money(i.replacement_total)}</td>
                  <td className={Math.abs(i.difference) > 0 ? "text-[color:var(--warning)] font-bold" : ""}>{money(i.difference)}</td>
                  <td>{i.manager_name}</td>
                  <td>
                    <Link to={`/float/${i.id}`} className="text-[color:var(--gold)] hover:underline">{i.reference_number}</Link>
                    <div><span className={`text-[10px] px-2 py-0.5 rounded-full border ${M.cls}`}>{M.label}</span></div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-[color:var(--text-secondary)]">No float changes yet.</td></tr>}
          </tbody>
        </table>

        <div className="md:hidden divide-y divide-[color:var(--border)]">
          {items.map((i) => {
            const M = STATUS_META[i.status] || STATUS_META.balanced;
            return (
              <Link to={`/float/${i.id}`} key={i.id} className="block p-4 hover:bg-[color:var(--bg-surface-2)]" data-testid={`float-card-${i.id}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-semibold">{i.register_name}</div>
                    <div className="text-xs text-[color:var(--text-secondary)]">{humanTime(i.created_at)}</div>
                  </div>
                  <span className={`text-[10px] px-2 py-1 rounded-full border ${M.cls}`}>{M.label}</span>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                  <div><div className="label-caps">Taken</div><div className="font-heading">{money(i.taken_total)}</div></div>
                  <div><div className="label-caps">Replaced</div><div className="font-heading">{money(i.replacement_total)}</div></div>
                </div>
                <div className="mt-2 flex justify-between items-center text-xs">
                  <span>{i.manager_name}</span>
                  <span className="text-[color:var(--gold)]">{i.reference_number}</span>
                </div>
              </Link>
            );
          })}
          {items.length === 0 && <div className="p-8 text-center text-[color:var(--text-secondary)]">No float changes yet.</div>}
        </div>
      </div>
    </div>
  );
}

function Card({ k, v, icon: Icon, accent }) {
  const color = accent === "warning" ? "text-[color:var(--warning)]" : accent === "emerald" ? "text-emerald-400" : "text-[color:var(--gold)]";
  return (
    <div className="hos-card p-4">
      <div className="flex items-center justify-between">
        <div className="label-caps">{k}</div>
        {Icon && <Icon size={14} className={color}/>}
      </div>
      <div className={`font-heading text-3xl ${color}`}>{v}</div>
    </div>
  );
}
